import { useEffect } from "react";

// Per-route SEO: title, description, canonical, OG/Twitter, optional noindex
// and optional JSON-LD. Restores previous values on unmount.
export default function useSeo({ title, description, path, noindex = false, jsonLd = null }) {
  useEffect(() => {
    const SITE = "https://webjesters.com";
    const url = path ? `${SITE}${path}` : window.location.href;

    const prevTitle = document.title;
    const setMeta = (selector, attrName, attrValue, content) => {
      let el = document.head.querySelector(selector);
      const created = !el;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attrName, attrValue);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute("content");
      el.setAttribute("content", content);
      return () => {
        if (created) el.remove();
        else if (prev !== null) el.setAttribute("content", prev);
      };
    };
    const setLink = (rel, href) => {
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      const created = !el;
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute("href");
      el.setAttribute("href", href);
      return () => {
        if (created) el.remove();
        else if (prev !== null) el.setAttribute("href", prev);
      };
    };

    if (title) document.title = title;

    const restorers = [];
    if (description) {
      restorers.push(
        setMeta('meta[name="description"]', "name", "description", description),
        setMeta('meta[property="og:description"]', "property", "og:description", description),
        setMeta('meta[name="twitter:description"]', "name", "twitter:description", description),
      );
    }
    if (title) {
      restorers.push(
        setMeta('meta[property="og:title"]', "property", "og:title", title),
        setMeta('meta[name="twitter:title"]', "name", "twitter:title", title),
      );
    }
    restorers.push(
      setMeta('meta[property="og:url"]', "property", "og:url", url),
      setLink("canonical", url),
    );
    if (noindex) {
      restorers.push(setMeta('meta[name="robots"]', "name", "robots", "noindex, nofollow"));
    }
    if (jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-route-jsonld", "true");
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
      restorers.push(() => script.remove());
    }

    return () => {
      document.title = prevTitle;
      restorers.forEach((fn) => fn && fn());
    };
  }, [title, description, path, noindex, jsonLd]);
}
